import React, { useEffect, useState } from 'react';
import { Wrench, Home, Camera } from 'lucide-react';

const P = (img, x, y, s, alt) => ({ type: 'photo', img, x, y, s, alt });
const I = (Icon, x, y, s, tone) => ({ type: 'icon', Icon, x, y, s, tone });
const D = (x, y, s, tone) => ({ type: 'dot', x, y, s, tone });

// Desktop cluster — one compact oval "cloud". x/y are % of the container,
// s is % of container height. Hierarchy: 4 large, 6 medium, 6 small photos.
const DESKTOP = [
  P('cleaning', 43, 23, 25.2, 'Home cleaner wiping a kitchen bench'),
  P('plumbing', 15, 58, 25.2, 'Plumber working under a sink'),
  P('gardening', 82, 50, 25.2, 'Gardener trimming a hedge'),
  P('beauty', 52, 81, 25.2, 'Hairdresser styling a client'),
  P('electrical', 49.7, 51.6, 17.2, 'Electrician testing a switchboard'),
  P('painting', 61.7, 36.8, 17.2, 'Painter rolling an interior wall'),
  P('removalist', 32.8, 44.9, 17.2, 'Removalist carrying a moving box'),
  P('pets', 35.7, 64.4, 17.2, 'Dog groomer with a golden retriever'),
  P('massage', 71.5, 70.9, 17.2, 'Massage therapist treating a client'),
  P('photography', 63.5, 16.7, 17.2, 'Photographer at an event'),
  P('handyman', 25.2, 29.9, 11.6, 'Handyman drilling a shelf'),
  P('ittech', 75.6, 29.9, 11.6, 'IT technician helping a customer with a laptop'),
  P('pest', 32.2, 81, 11.6, 'Pest control technician treating a home'),
  P('aircon', 13.6, 36.3, 11.6, 'Technician servicing an air conditioner heat pump'),
  P('dj', 19.7, 78.6, 11.6, 'DJ performing at an event'),
  P('propertymaint', 87.1, 70.3, 11.6, 'Window cleaning on a house exterior'),
  P('pool', 88.5, 30.1, 12, 'Pool cleaning technician'),
  I(Camera, 62.8, 52.1, 6.8, 'blue'),
  I(Home, 68.4, 85.5, 6.8, 'amber'),
  I(Wrench, 26.7, 18.2, 6.8, 'white'),
  D(3, 48, 2.4, 'muted'),
  D(98, 40, 2.4, 'amber'),
  D(47, 4, 2.4, 'blue'),
  D(51, 97, 2.4, 'amber'),
  D(7, 74, 2.4, 'blue'),
  D(94, 84, 2.4, 'muted'),
  D(30, 95, 2.4, 'amber'),
  D(88, 15, 2.4, 'muted'),
  D(14, 14, 2.4, 'amber'),
];

// Compact strips (below 1280px): tablet shows 9, mobile 7 (6 on very narrow phones).
const STRIP = [
  ['cleaning', 'Home cleaning'],
  ['plumbing', 'Plumbing'],
  ['beauty', 'Hair & beauty'],
  ['gardening', 'Gardening'],
  ['pets', 'Pet services'],
  ['massage', 'Massage & wellness'],
  ['photography', 'Photography'],
  ['electrical', 'Electrical'],
  ['dj', 'DJ & MC services'],
];

const getMode = () => {
  if (window.matchMedia('(min-width: 1280px)').matches) return 'desktop';
  if (window.matchMedia('(min-width: 768px)').matches) return 'tablet';
  return 'mobile';
};

const useMode = () => {
  const [mode, setMode] = useState(getMode);
  useEffect(() => {
    const mqs = ['(min-width: 1280px)', '(min-width: 768px)'].map((q) => window.matchMedia(q));
    const onChange = () => setMode(getMode());
    mqs.forEach((mq) => mq.addEventListener('change', onChange));
    return () => mqs.forEach((mq) => mq.removeEventListener('change', onChange));
  }, []);
  return mode;
};

const src = (name) => `/hero/${name}.webp`;

export const HeroMosaic = () => {
  const mode = useMode();

  if (mode !== 'desktop') {
    const items = mode === 'tablet' ? STRIP : STRIP.slice(0, 7);
    return (
      <div className={`hero-mosaic-strip strip-${mode}`} data-testid={`hero-mosaic-${mode}`}>
        {items.map(([name, alt], i) => (
          <span key={name} className={`mosaic-m-item ${i % 2 ? 'down' : 'up'}`}>
            <img src={src(name)} alt={alt} width="64" height="64" loading="lazy" decoding="async" />
          </span>
        ))}
      </div>
    );
  }

  return (
    <div className="hero-mosaic" data-testid="hero-mosaic">
      {DESKTOP.map((it, i) => {
        const style = { left: `${it.x}%`, top: `${it.y}%`, height: `${it.s}%`, animationDelay: `${(i % 6) * 0.7}s` };
        if (it.type === 'photo') {
          return (
            <span key={it.img} className="mosaic-photo" style={style}>
              <img src={src(it.img)} alt={it.alt} width="240" height="240" loading={i < 4 ? 'eager' : 'lazy'} decoding="async" />
            </span>
          );
        }
        if (it.type === 'icon') {
          const Icon = it.Icon;
          return (
            <span key={`i${i}`} className={`mosaic-icon tone-${it.tone}`} style={style} aria-hidden="true">
              <Icon strokeWidth={2.2} />
            </span>
          );
        }
        return <span key={`d${i}`} className={`mosaic-dot tone-${it.tone}`} style={style} aria-hidden="true" />;
      })}
    </div>
  );
};
